import React, { useRef } from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import { alpha } from "@mui/material/styles";
import { OtpInputProps } from "types";
import { useText } from "context/TextProvider";

const OtpInput = ({ otp, setOtp, acceptText = false, boxCount = 6 }: OtpInputProps) => {
  const text = useText();
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);

  const handlePaste = (e: React.ClipboardEvent<HTMLElement>) => {
    e.preventDefault();
    let pastedData = e.clipboardData.getData("text");
    pastedData = acceptText
      ? pastedData.replace(/[^a-zA-Z0-9]/g, "")
      : pastedData.replace(/\D/g, "");
    pastedData = pastedData.slice(0, boxCount);
    if (!pastedData) return;
    setOtp(pastedData);
    const lastIndex = Math.min(pastedData.length - 1, boxCount - 1);
    inputsRef.current[lastIndex]?.focus();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, index: number) => {
    let value = e.target.value;
    value = acceptText ? value.replace(/[^a-zA-Z0-9]/g, "") : value.replace(/\D/g, "");
    if (value.length > 1) {
      const fullCode = value.slice(0, boxCount);
      setOtp(fullCode);
      inputsRef.current[Math.min(fullCode.length - 1, boxCount - 1)]?.focus();
      return;
    }
    if (!value) return;
    const otpArray = otp.split("");
    otpArray[index] = value;
    setOtp(otpArray.join(""));
    if (index < boxCount - 1) inputsRef.current[index + 1]?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLElement>, index: number) => {
    if (e.key === "Backspace") {
      if (!otp[index] && index > 0) {
        inputsRef.current[index - 1]?.focus();
        return;
      }
      const otpArray = otp.split("");
      otpArray[index] = "";
      setOtp(otpArray.join(""));
    }
  };

  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", gap: "12px", mb: 4 }}>
      {Array.from({ length: boxCount }).map((_, i) => (
        <TextField
          key={i}
          type="text"
          inputRef={(el) => { inputsRef.current[i] = el; }}
          value={otp[i] || ""}
          onPaste={i === 0 ? handlePaste : undefined}
          onChange={(e) => handleInputChange(e, i)}
          onKeyDown={(e) => handleKeyDown(e, i)}
          variant="outlined"
          className="login-config-input"
          slotProps={{
            htmlInput: {
              inputMode: acceptText ? "text" : "numeric",
              autoComplete: i === 0 ? "one-time-code" : "off",
              maxLength: i === 0 ? boxCount : 1,
              "aria-label": `${text.otp.digitAriaLabelPrefix} ${i + 1}`,
              style: { textAlign: "center" },
            },
          }}
          sx={(theme) => ({
            width: 48,
            "& .MuiOutlinedInput-root": {
              height: 56,
              transition: "all 150ms ease",
              "& fieldset": {
                borderColor: otp[i] ? theme.palette.primary.main : theme.palette.divider,
              },
              "&:hover fieldset": {
                borderColor: otp[i] ? theme.palette.primary.main : theme.palette.divider,
              },
              "&.Mui-focused fieldset": {
                borderColor: theme.palette.primary.main,
              },
              "&.Mui-focused": {
                boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.4)}`,
              },
            },
            "& .MuiInputBase-input": {
              height: "100%",
              padding: 0,
              textAlign: "center",
            },
          })}
        />
      ))}
    </Box>
  );
};

export default OtpInput;
